const React = require('react');
const { createElement: h, useState, useCallback } = React;
require('./WorkflowVersionHistory.css');

// Inline version-history control for the workflow editor header. Renders a
// "History" button that toggles an inline dropdown panel (no modal overlay, per
// the no-popup rule) listing past saves newest-first, each with a Restore
// action. Restore goes through the normal save path server-side, so it is
// non-destructive: it becomes the current state AND a new version entry.
//
// Props:
//   workflowId   string  — the open workflow's id (required to fetch/restore)
//   listVersions (id) => Promise<Array<{version, workflowName, createdAt}>>
//   restoreVersion (id, version) => Promise<state>
//   onRestored   (state) => void  — called after a successful restore so the
//                                    canvas can rehydrate to the restored state
//   getLiveVersion (id) => Promise<{version, publishedAt}|null>  — optional; when
//                  provided, a pinned read-only "Live vX" info row is shown on top
function HistoryIcon() {
  return h('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M3 3v5h5' }),
    h('path', { d: 'M3.05 13A9 9 0 1 0 6 5.3L3 8' }),
    h('path', { d: 'M12 7v5l4 2' })
  );
}

function formatWhen(value) {
  if (!value) return '';
  var d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function WorkflowVersionHistory(props) {
  var workflowId = props && props.workflowId ? String(props.workflowId) : '';
  var listVersions = props && typeof props.listVersions === 'function' ? props.listVersions : null;
  var restoreVersion = props && typeof props.restoreVersion === 'function' ? props.restoreVersion : null;
  var onRestored = props && typeof props.onRestored === 'function' ? props.onRestored : function() {};
  var getLiveVersion = props && typeof props.getLiveVersion === 'function' ? props.getLiveVersion : null;
  var listLiveHistory = props && typeof props.listLiveHistory === 'function' ? props.listLiveHistory : null;

  // Current live (production) version metadata, shown as a pinned info row.
  var liveState = useState(null);
  var live = liveState[0];
  var setLive = liveState[1];

  // Last 10 published live versions (metadata) for the "Live versions" section.
  var liveHistoryState = useState([]);
  var liveHistory = liveHistoryState[0];
  var setLiveHistory = liveHistoryState[1];

  var openState = useState(false);
  var open = openState[0];
  var setOpen = openState[1];

  var versionsState = useState([]);
  var versions = versionsState[0];
  var setVersions = versionsState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  // Which version is currently being restored (disables its row + shows label).
  var restoringState = useState(0);
  var restoring = restoringState[0];
  var setRestoring = restoringState[1];

  var refresh = useCallback(function() {
    if (!workflowId || !listVersions) return;
    setLoading(true);
    setError('');
    listVersions(workflowId)
      .then(function(rows) {
        setVersions(Array.isArray(rows) ? rows : []);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not load history.');
      })
      .finally(function() {
        setLoading(false);
      });
    // Live metadata (best-effort, independent of the version list).
    if (getLiveVersion) {
      getLiveVersion(workflowId)
        .then(function(info) { setLive(info || null); })
        .catch(function() { setLive(null); });
    }
    // Live version history (best-effort).
    if (listLiveHistory) {
      listLiveHistory(workflowId)
        .then(function(rows) { setLiveHistory(Array.isArray(rows) ? rows : []); })
        .catch(function() { setLiveHistory([]); });
    }
  }, [workflowId, listVersions, getLiveVersion, listLiveHistory]);

  function toggle() {
    var next = !open;
    setOpen(next);
    if (next) refresh();
  }

  function handleRestore(version) {
    if (!workflowId || !restoreVersion || restoring) return;
    setRestoring(version);
    setError('');
    restoreVersion(workflowId, version)
      .then(function(state) {
        if (state) onRestored(state);
        setOpen(false);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not restore this version.');
      })
      .finally(function() {
        setRestoring(0);
      });
  }

  if (!workflowId || !listVersions || !restoreVersion) return null;

  return h('div', { className: 'wf-version-wrap' },
    h('button', {
      className: 'header-btn header-text-btn wf-version-trigger' + (open ? ' wf-version-trigger-open' : ''),
      onClick: toggle,
      title: 'View and restore previous saved versions'
    },
      h(HistoryIcon),
      h('span', { className: 'header-btn-label' }, 'History')
    ),
    open
      ? h('div', {
          className: 'wf-version-dropdown',
          onMouseDown: function(e) { e.stopPropagation(); },
          onTouchStart: function(e) { e.stopPropagation(); }
        },
          h('div', { className: 'wf-version-backdrop', onClick: function() { setOpen(false); } }),
          h('div', { className: 'wf-version-panel' },
            h('div', { className: 'wf-version-header' },
              h('span', { className: 'wf-version-title' }, 'Version history'),
              h('button', {
                className: 'wf-version-refresh',
                onClick: refresh,
                disabled: loading,
                title: 'Refresh'
              }, loading ? 'Loading…' : 'Refresh')
            ),
            getLiveVersion
              ? h('div', { className: 'wf-version-live-row' },
                  h('span', { className: 'wf-version-live-badge' }, 'LIVE'),
                  live && live.version
                    ? h('span', { className: 'wf-version-live-text' },
                        'v' + live.version,
                        live.publishedAt
                          ? h('span', { className: 'wf-version-live-when' }, ' · published ' + formatWhen(live.publishedAt))
                          : null
                      )
                    : h('span', { className: 'wf-version-live-text wf-version-live-none' }, 'Not published yet')
                )
              : null,
            // Live version history (last 10 published live versions), read-only.
            (listLiveHistory && liveHistory.length > 0)
              ? h('div', { className: 'wf-version-live-history' },
                  h('div', { className: 'wf-version-subhead' }, 'Live versions'),
                  liveHistory.map(function(lv) {
                    return h('div', { className: 'wf-version-row', key: lv.id },
                      h('div', { className: 'wf-version-row-main' },
                        h('span', { className: 'wf-version-num wf-version-num-live' }, 'v' + lv.version),
                        h('span', { className: 'wf-version-name' }, lv.workflowName || 'Untitled')
                      ),
                      h('div', { className: 'wf-version-row-meta' },
                        h('span', { className: 'wf-version-when' }, formatWhen(lv.createdAt))
                      )
                    );
                  })
                )
              : null,
            error ? h('div', { className: 'wf-version-error' }, error) : null,
            (listLiveHistory || versions.length > 0)
              ? h('div', { className: 'wf-version-subhead' }, 'Staging saves')
              : null,
            !loading && !error && versions.length === 0
              ? h('div', { className: 'wf-version-empty' }, 'No saved versions yet. They appear here after each save.')
              : null,
            h('div', { className: 'wf-version-list' },
              versions.map(function(v) {
                var isRestoringThis = restoring === v.version;
                return h('div', { className: 'wf-version-row', key: v.version },
                  h('div', { className: 'wf-version-row-main' },
                    h('span', { className: 'wf-version-num' }, 'v' + v.version),
                    h('span', { className: 'wf-version-name' }, v.workflowName || 'Untitled')
                  ),
                  h('div', { className: 'wf-version-row-meta' },
                    h('span', { className: 'wf-version-when' }, formatWhen(v.createdAt)),
                    h('button', {
                      className: 'wf-version-restore',
                      onClick: function() { handleRestore(v.version); },
                      disabled: !!restoring,
                      title: 'Restore this version as the current workflow'
                    }, isRestoringThis ? 'Restoring…' : 'Restore')
                  )
                );
              })
            )
          )
        )
      : null
  );
}

module.exports = WorkflowVersionHistory;
